import {
  useEffect,
  useRef
} from 'react'

export const useSSE = ({
  url,
  token,
  profileId,
  onMessage,
  onReconnect
}) => {
  const onMessageRef = useRef(onMessage)
  onMessageRef.current = onMessage

  const onReconnectRef = useRef(onReconnect)
  onReconnectRef.current = onReconnect

  useEffect(() => {
    if (!token || !profileId) return () => {}

    const controller = new AbortController()

    const wait = (ms) => new Promise((resolve) => {
      const timer = setTimeout(resolve, ms)
      controller.signal.addEventListener('abort', () => {
        clearTimeout(timer)
        resolve()
      })
    })

    const connect = async () => {
      let attempt = 0
      let hasConnected = false

      while (!controller.signal.aborted) {
        try {
          // eslint-disable-next-line no-await-in-loop
          const response = await fetch(url, {
            headers: { Authorization: `Bearer ${token}` },
            signal: controller.signal
          })

          if (!response.ok) {
            console.error(`SSE connection failed: ${response.status}`)
            if (response.status === 401 || response.status === 403) return
            throw new Error(`HTTP ${response.status}`)
          }

          attempt = 0

          if (hasConnected) {
            onReconnectRef.current?.()
          }
          hasConnected = true

          const reader = response.body.getReader()
          const decoder = new TextDecoder()
          let buffer = ''

          // eslint-disable-next-line no-constant-condition
          while (true) {
            // eslint-disable-next-line no-await-in-loop
            const { value, done } = await reader.read()
            if (done) break

            buffer += decoder.decode(value, { stream: true })
            const parts = buffer.split('\n\n')
            buffer = parts.pop()

            for (const part of parts) {
              const line = part.split('\n').find((l) => l.startsWith('data: '))
              if (line) {
                try {
                  const payload = JSON.parse(line.slice(6))
                  onMessageRef.current(payload)
                } catch (error) {
                  console.error('Invalid SSE payload:', error)
                }
              }
            }
          }

          console.warn('SSE stream closed, reconnecting...')
        } catch (err) {
          if (err.name === 'AbortError') return
          console.error('SSE stream error:', err.message)
        }

        if (controller.signal.aborted) return

        const delay = Math.min(1000 * 2 ** attempt, 30000)
        attempt += 1
        // eslint-disable-next-line no-await-in-loop
        await wait(delay + Math.random() * delay * 0.3)
      }
    }

    connect()

    return () => {
      controller.abort()
    }
  }, [url, token, profileId])
}
